import { type APINotifications } from "@api/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Button,
  Badge,
} from "@ui/components";
import { cn } from "@ui/lib/utils";
import dayjs from "dayjs";
import { Bell } from "iconoir-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useAuthStore } from "src/auth";
import { useNotificationsQuery } from "src/queries/notifications";
import { trpcClient } from "src/trpc/client";

export function NotificationButton() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const authStore = useAuthStore();

  const notificationsQuery = useNotificationsQuery();

  const [isOpen, setIsOpen] = useState(false);

  const notifications = notificationsQuery.data?.notifications ?? [];
  const unreadCount = notificationsQuery.data?.unreadCount ?? 0;

  const markAllAsReadMutation = useMutation({
    mutationFn: () => {
      return trpcClient.notifications.markAllAsRead.mutate();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const markAsReadMutation = useMutation({
    mutationFn: (id: string) => {
      return trpcClient.notifications.markAsRead.mutate({ notificationId: id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  function handleNotificationClick(
    notification: APINotifications.Notification,
  ) {
    markAsReadMutation.mutate(notification.id);
    switch (notification.type) {
      case "COMMENT_ADDED":
        {
          if (notification.comment?.calendarItemId) {
            if (authStore.isCoach) {
              navigate(
                `/athletes/${notification.senderId}/calendar/${notification.comment.calendarItemId}/edit-workout?comments=${notification.comment.workoutItemId}`,
              );
              return;
            } else {
              // http://localhost:3000/calendar/1024b9d5-4ee1-4cbb-abe0-5eba33e26b25/view?comments=81c7275c-becc-41a7-a324-01da4ff112c5
              navigate(
                `/calendar/${notification.comment.calendarItemId}/view?comments=${notification.comment.workoutItemId}`,
              );
            }
          }
        }
        break;
      default:
        break;
    }

    throw new Error("Unsupported notification entity");
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="relative p-0 size-10 rounded-full">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge className="absolute -top-1 -right-1 px-1 h-5 min-w-[20px] flex items-center justify-center bg-red-500 text-white text-xs">
              {unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="border-0 shadow-none">
          <div className="pb-2 pt-4 px-4 flex flex-col items-center">
            <div className="text-lg font-medium">{t("notifications")}</div>
            {unreadCount > 0 && (
              <div>
                <Button
                  variant="ghost"
                  className="text-xs h-auto p-1 px-3"
                  onClick={() => {
                    markAllAsReadMutation.mutate();
                  }}
                >
                  {t("markAllAsRead")}
                </Button>
              </div>
            )}
          </div>
          <div className="p-0">
            {notifications.length === 0 ? (
              <div className="py-6 px-4 text-center text-muted-foreground">
                {t("noNotifications")}
              </div>
            ) : (
              <div className="max-h-80 overflow-auto">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={cn(
                      `flex items-start gap-4 p-4 border-b last:border-b-0 cursor-pointer hover:bg-slate-50 `,
                      { "bg-slate-50": !notification.read },
                    )}
                    onClick={() => {
                      handleNotificationClick(notification);
                    }}
                  >
                    <div className="flex-1">
                      <div
                        className={cn("flex flex-col justify-between", {
                          "opacity-30": notification.read,
                        })}
                      >
                        <p
                          className={`text-sm ${!notification.read ? "font-semibold" : ""}`}
                        >
                          {`${t("commentAdded", {
                            author: notification.sender.name,
                          })} `}
                          <span className="font-bold">
                            {notification.comment?.workoutName ?? "-"}
                          </span>
                        </p>
                        <span className="text-xs text-slate-500">
                          {dayjs(notification.createdAt).format("HH:mm MMM DD")}
                        </span>
                      </div>
                    </div>
                    {!notification.read && (
                      <div className="mt-1 h-2 w-2 rounded-full bg-blue-500" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="p-2 bg-slate-50 border-t rounded-b-md">
            <Button
              variant="ghost"
              className="w-full text-sm h-auto py-1.5"
              onClick={() => setIsOpen(false)}
            >
              {t("viewAllNotifications")}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
